"use client";

import { Skeleton } from "design-system/skeleton";
import dynamic from "next/dynamic";
import { LazyLoadDialog } from "@/components/custom/lazy-load-component";
import { useSepaAccountsEnabled } from "@/lib/hooks/flags/use-sepa-accounts-enabled";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";

export function BankAccountsAddFormDialog({ dialogControls }: { dialogControls: UseDialogReturn<void> }) {
  const isSepaEnabled = useSepaAccountsEnabled();

  return (
    <LazyLoadDialog
      description={
        isSepaEnabled
          ? "Connect a bank account to send or receive USD, or EUR over SEPA."
          : "Connect a bank account to send or receive USD. Agora only supports USD accounts."
      }
      dialogControls={dialogControls}
      title="Add bank account"
    >
      <BankAccountsAddForm
        dialogControls={dialogControls}
        onSuccess={() => dialogControls?.dismiss()}
        redirectOnSuccess={false}
      />
    </LazyLoadDialog>
  );
}

const BankAccountsAddForm = dynamic(
  () =>
    import("@/subsystems/transfer/bank-accounts/pages/bank-accounts-add-form").then((mod) => mod.BankAccountsAddForm),
  {
    loading: () => (
      <div className="w-full space-y-6">
        {/* Account Nickname Section */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-4 w-96" />
        </div>

        {/* Name on Account Section */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-4 w-80" />
        </div>

        {/* Account Number Section */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        {/* Routing Number Section */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        {/* Bank Name Section */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        {/* Buttons Section */}
        <div className="flex justify-between">
          <Skeleton className="h-10 w-40 rounded-md" />
          <Skeleton className="h-10 w-40 rounded-md" />
        </div>
      </div>
    ),
    ssr: false,
  },
);
